import { DEFAULT_LANGUAGE, FullCV, TemplateId, getTranslation } from '@cibli/shared';
import i18n from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { useCVTemplateContext } from '@/features/common/contexts/cv-template.context';
import { CV_EXEMPLE } from '@/features/cv/CV_exemple';
import { registerAllTemplates } from '@/features/cv/templates/register';
import { getTemplate } from '@/features/cv/templates/registry';
import TemplateHost from '@/features/cv/templates/TemplateHost';
import api from '@/services/api';

registerAllTemplates();

/**
 * Test-only page: renders a CV in a specified language for Playwright e2e tests.
 * Usage: /cv/test-translation?lang=uk-UA&template=compact
 * Supported params: lang (i18n locale code), template (template id)
 */
export default function CVTranslationTestPage() {
  const [searchParams] = useSearchParams();
  const { setCurrentLocaleCv } = useCVTemplateContext();
  const [isReady, setIsReady] = useState(false);

  const lang = searchParams.get('lang') || DEFAULT_LANGUAGE;
  const templateId = (searchParams.get('template') || 'compact') as TemplateId;

  const template = useMemo(() => getTemplate(templateId), [templateId]);
  const params = useMemo(() => template.defaults, [template]);

  const cv = useMemo(() => {
    const base = CV_EXEMPLE as unknown as Record<string, unknown>;
    return {
      ...base,
      hasRQTH: CV_EXEMPLE.complementaryInfo?.hasRQTH ?? false,
      complementaryMentions: CV_EXEMPLE.complementaryInfo?.mentions ?? [],
      template: templateId,
      templateParams: params,
    } as unknown as FullCV;
  }, [templateId, params]);

  useEffect(() => {
    if (i18n.language === lang && i18n.hasResourceBundle(lang, 'translation')) {
      setCurrentLocaleCv(lang);
      setIsReady(true);
      return;
    }

    const load = async () => {
      if (!i18n.hasResourceBundle(lang, 'translation')) {
        try {
          const data = await getTranslation(lang, {
            api,
            useRemote: import.meta.env.VITE_USE_WEBLATE === 'true',
          });
          if (data.translations) {
            i18n.addResourceBundle(lang, 'translation', data.translations, true, true);
          }
        } catch (error) {
          console.error(`Failed to load translation for ${lang}:`, error);
        }
      }
      await i18n.changeLanguage(lang);
      setCurrentLocaleCv(lang);
      setIsReady(true);
    };

    void load();
  }, [lang, i18n, setCurrentLocaleCv]);

  if (!isReady) {
    return <div data-testid="loading">Loading translations for {lang}...</div>;
  }

  return (
    <div className="w-full flex justify-center bg-white min-h-screen">
      <div className="w-[210mm] min-h-[297mm]">
        <TemplateHost cv={cv} mode="print" templateId={templateId} initialParams={params} />
      </div>
    </div>
  );
}
